// Cost model for a report run. Token prices come from the live feed (priceFeed.ts) when it has the model, else
// from the hand-written table below (list prices checked 2026-09-16, design/MarketReport.md "成本测算"); actual
// cost comes from the usage the provider returns. Unknown models fall back to the search fee only.
import type { Provider } from '@/types';
import { lookupFeed, priceFeed, type PriceFeed } from './priceFeed';

export interface Price {
  input: number; // USD per 1M tokens
  output: number;
  searchFee: number; // USD per search call
  searchTokens: number; // tokens one search injects into the context
  source: 'feed' | 'builtin';
  approx?: boolean; // feed price taken from a reseller's row of the same model name
}

export const BUILTIN_PRICES_AS_OF = '2026-09-16';

// Per-provider search behaviour the feed does not describe: how much context a search injects, and Gemini's
// free quota (5,000 queries/month on 3.x, 1,500/day on 2.5) that personal use never exceeds.
const SEARCH_TOKENS: Record<Provider, number> = { openai: 8000, claude: 5000, gemini: 3000 };
const DEFAULT_SEARCH_FEE: Record<Provider, number> = { openai: 0.01, claude: 0.01, gemini: 0 };

type Builtin = Omit<Price, 'source'>;
const TABLE: { provider: Provider; match: RegExp; price: Builtin }[] = [
  { provider: 'openai', match: /gpt-5\.5/i, price: { input: 5, output: 30, searchFee: 0.01, searchTokens: 8000 } },
  { provider: 'openai', match: /gpt-5\.4/i, price: { input: 2.5, output: 15, searchFee: 0.01, searchTokens: 8000 } },
  { provider: 'openai', match: /gpt-5.*nano/i, price: { input: 0.05, output: 0.4, searchFee: 0.01, searchTokens: 8000 } },
  { provider: 'openai', match: /gpt-5.*mini/i, price: { input: 0.25, output: 2, searchFee: 0.01, searchTokens: 8000 } },
  { provider: 'openai', match: /gpt-5/i, price: { input: 1.25, output: 10, searchFee: 0.01, searchTokens: 8000 } },
  { provider: 'openai', match: /^o\d/i, price: { input: 2, output: 8, searchFee: 0.01, searchTokens: 8000 } },
  { provider: 'openai', match: /gpt-4\.1/i, price: { input: 2, output: 8, searchFee: 0.025, searchTokens: 0 } },
  { provider: 'claude', match: /opus/i, price: { input: 5, output: 25, searchFee: 0.01, searchTokens: 5000 } },
  { provider: 'claude', match: /sonnet-4/i, price: { input: 3, output: 15, searchFee: 0.01, searchTokens: 5000 } },
  { provider: 'claude', match: /sonnet/i, price: { input: 2, output: 10, searchFee: 0.01, searchTokens: 5000 } },
  { provider: 'claude', match: /haiku/i, price: { input: 1, output: 5, searchFee: 0.01, searchTokens: 5000 } },
  // Gemini search is free within 5,000 requests/month (3.x) or 1,500/day (2.5), far above personal use.
  { provider: 'gemini', match: /gemini-3.*pro/i, price: { input: 2, output: 12, searchFee: 0, searchTokens: 3000 } },
  { provider: 'gemini', match: /gemini-3/i, price: { input: 0.75, output: 3.75, searchFee: 0, searchTokens: 3000 } },
  { provider: 'gemini', match: /gemini-2\.5-pro/i, price: { input: 1.25, output: 10, searchFee: 0, searchTokens: 3000 } },
  { provider: 'gemini', match: /flash-lite/i, price: { input: 0.1, output: 0.4, searchFee: 0, searchTokens: 3000 } },
  { provider: 'gemini', match: /gemini/i, price: { input: 0.3, output: 2.5, searchFee: 0, searchTokens: 3000 } },
];

export function priceFor(provider: Provider, model: string, feed: PriceFeed | null = priceFeed()): Price | null {
  const live = lookupFeed(feed, provider, model);
  if (live) return { input: live.price.input, output: live.price.output, searchFee: provider === 'gemini' ? 0 : (live.price.search ?? DEFAULT_SEARCH_FEE[provider]), searchTokens: SEARCH_TOKENS[provider], source: 'feed', approx: live.approx };
  const builtin = TABLE.find((row) => row.provider === provider && row.match.test(model))?.price;
  return builtin ? { ...builtin, source: 'builtin' } : null;
}

const PROMPT_TOKENS = 2200;
const OUTPUT_TOKENS = 7000; // ~1,500 chars of report plus reasoning
const LOOP_TURNS = 4; // Claude/OpenAI re-read the context each server-side search turn; Gemini bills once

/** Rough per-run cost before generating; null when the model is not in the price table. */
export function estimateCost(provider: Provider, model: string, searches: number, feed: PriceFeed | null = priceFeed()): { usd: number | null; searchFee: number } {
  const p = priceFor(provider, model, feed);
  if (!p) return { usd: null, searchFee: searches * 0.01 };
  const searchFee = searches * p.searchFee;
  const inputTokens = provider === 'gemini' ? PROMPT_TOKENS + searches * p.searchTokens : PROMPT_TOKENS * LOOP_TURNS + (searches * p.searchTokens * LOOP_TURNS) / 2;
  const usd = searchFee + (inputTokens * p.input + OUTPUT_TOKENS * p.output) / 1e6;
  return { usd, searchFee };
}

/** Cost from the provider's returned usage; null when the price is unknown or nothing was reported. */
export function actualCost(provider: Provider, model: string, usage?: { inputTokens?: number; outputTokens?: number; searches?: number }, feed: PriceFeed | null = priceFeed()): number | null {
  const p = priceFor(provider, model, feed);
  if (!p || !usage || (usage.inputTokens == null && usage.outputTokens == null && usage.searches == null)) return null;
  return (usage.searches ?? 0) * p.searchFee + ((usage.inputTokens ?? 0) * p.input + (usage.outputTokens ?? 0) * p.output) / 1e6;
}

export function formatUsd(usd: number | null | undefined): string {
  if (usd == null) return '—';
  return usd < 0.01 ? '<$0.01' : `$${usd.toFixed(2)}`;
}
